/**
 * PatientPicker (PROD-579 + PROD-569 wiring + PROD-584 props contract +
 * PROD-585 demo-gap surface).
 *
 * Sidebar list of synthetic patients. Renders inside ClinicalDesk on the
 * left rail. Pure presentational component: receives the registry entry
 * list, the active id, and a click handler from its parent. The store
 * wiring (telemetry emission, persistence, cross-store invariants) lives
 * in `usePatientStore`; this component never reads from or writes to it.
 *
 * Props contract per docs/COMPONENT-CONTRACTS.md §6 (PROD-584):
 *   interface PatientPickerProps {
 *     patients: ReadonlyArray<PatientRegistryEntry>;
 *     activePatientId: string | null;
 *     onSelect: (patientId: string) => void;
 *     className?: string;
 *   }
 *
 * Why props-driven (PROD-584 motivation):
 *   1. PROD-568 tenant switcher needs to pass `patients={getPatientsForTenant(activeTenant)}`
 *      so the picker honours tenant scoping without recomputing inside.
 *   2. Testability: no Zustand setup required to render the component.
 *   3. Single source of truth: COMPONENT-CONTRACTS §6 is canonical.
 *
 * The `patients` array accepts `PatientRegistryEntry` (the union of a
 * valid `PatientFixture` and a `MissingPatientFixture` sentinel) so the
 * caller can pass `PATIENT_REGISTRY` directly and let the picker render
 * the demo-gap surface for any slot whose fixture failed Zod validation
 * (PROD-585).
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §6:
 *   - Picker list: Card (List) - DS-existing or [GAP]
 *   - Patient row: Card (Patient-row) - [GAP: signal-ds-package]
 *   - Selected highlight: Token color (`primary-soft`) - DS-existing
 *   - Missing-fixture row: Token color (`warning-strong`) - DS-existing
 *     via `src/styles/tokens.ts`. The warning-strong palette is the same
 *     hue family the chart-header SyntheticBadge uses (mitigation 1) so
 *     the missing-fixture surface is unmistakable in a scannable list.
 *
 * HC2: Tailwind classes resolve through the Signal DS token map in
 * `src/styles/tokens.ts`. No raw hex.
 *
 * HC1 + ADR-PROTO-002 mitigation 2 (PROD-569 wiring):
 *   - Every name surfaces through `<PatientNameTag>` which appends the
 *     tokenized `[SYNTHETIC]` suffix in the warning-soft palette.
 *   - Mitigation 1 (the warning-strong chart-header `<SyntheticBadge>`)
 *     lives on the patient chart in ClinicalDesk and is the canonical SR
 *     announcement; rendering it again on every list row produced visual
 *     noise (red badge + amber suffix on the same row), so the inline-row
 *     variant is intentionally NOT rendered here.
 *
 * HC1 + master v3.3 amendment Demo gap field (PROD-585):
 *   - The picker iterates the entries it receives. If an entry is a
 *     `MissingPatientFixture` sentinel, the slot renders as a disabled
 *     row with the canonical
 *     `[FIXTURE MISSING - {patientId} fixture not loaded. The 4 canonical
 *      profiles required: khalid-al-mansoori, fatima-al-zaabi,
 *      omar-al-rashidi, saeed-al-mazrouei.]`
 *     tooltip per COMPONENT-CONTRACTS §6. Other rows remain selectable
 *     (graceful degradation, app boots).
 *
 * HC5: zero em dashes anywhere in the rendered copy.
 */
import {
  isMissingPatientFixture,
  patientFixtureMissingTooltip,
  type PatientRegistryEntry,
  type MissingPatientFixture,
} from '../data/synthetic-patients';
import type { PatientFixture } from '../data/synthetic-patients/types';
import { PatientNameTag } from './PatientNameTag';

export interface PatientPickerProps {
  /** Patients to render in canonical order. May include `MissingPatientFixture` sentinels (PROD-585) so callers can pass `PATIENT_REGISTRY` directly. Caller (e.g. PROD-568 tenant switcher) controls scoping. */
  patients: ReadonlyArray<PatientRegistryEntry>;
  /** Currently active patient id. `null` when no patient is selected. */
  activePatientId: string | null;
  /** Click handler. Receives the row's patient id; the caller decides what to do (typically `usePatientStore.setActivePatient`). */
  onSelect: (patientId: string) => void;
  /** Optional class hook for the outer `<aside>`. */
  className?: string;
}

interface PatientRowProps {
  patient: PatientFixture;
  isActive: boolean;
  onSelect: (id: string) => void;
}

/**
 * Short-form pill label for the picker row. Brand Patch (vault: 06-
 * decisions/care-pathway-studio/2026-05-06-brand-and-naming-cleanup-
 * patch.md) replaces the legacy TLP1-TLP4 pills with their clinical
 * pathway display names; the picker pill uses a compact form so the
 * row layout still fits the sidebar.
 */
const PATHWAY_PICKER_LABELS: Record<string, string> = {
  'cardiometabolic-pathway': 'Cardiometabolic',
  'hrt-pathway': 'HRT',
  'metabolic-syndrome-pathway': 'Metabolic Syndrome',
  'cvd-prevention-pathway': 'CVD Prevention',
};

function PatientRow({ patient, isActive, onSelect }: PatientRowProps) {
  const pathwayLabel = PATHWAY_PICKER_LABELS[patient.pathway] ?? 'Pathway';
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={isActive}
        data-patient-id={patient.id}
        data-pathway={patient.pathway}
        data-missing="false"
        onClick={() => onSelect(patient.id)}
        className={[
          'flex w-full flex-col gap-gutter-sm rounded-signal-md border px-gutter-md py-gutter-md text-left transition-colors',
          isActive
            ? 'border-signal-primary-500 bg-signal-primary-50'
            : 'border-surface-border bg-surface-base hover:bg-surface-muted',
        ].join(' ')}
      >
        <div className="flex items-center justify-between gap-gutter-sm">
          <PatientNameTag name={patient.name} variant="default" />
          <span className="rounded-signal-pill bg-signal-primary-100 px-gutter-sm py-0.5 text-signal-xs font-semibold text-signal-primary-700">
            {pathwayLabel}
          </span>
        </div>
        <div className="flex items-center gap-gutter-sm text-signal-xs text-text-tertiary">
          <span>{patient.age}</span>
          <span aria-hidden="true">·</span>
          <span>{patient.sex}</span>
          <span aria-hidden="true">·</span>
          <span className="capitalize">
            {patient.tenant.replace(/-/g, ' ')}
          </span>
        </div>
        <p className="text-signal-sm text-text-secondary">{patient.summary}</p>
      </button>
    </li>
  );
}

interface MissingPatientRowProps {
  sentinel: MissingPatientFixture;
}

/**
 * Demo-gap row per COMPONENT-CONTRACTS §6 + master v3.3 amendment
 * (PROD-585). Disabled, carries the canonical `[FIXTURE MISSING - ...]`
 * tooltip, styled in the warning-strong palette so it is unmistakable in
 * the scannable patient list. The visible inline copy keeps the gap
 * honest even when the user does not hover the tooltip; the tooltip
 * carries the full canonical sentence for screen readers and power
 * users.
 */
function MissingPatientRow({ sentinel }: MissingPatientRowProps) {
  const tooltip = patientFixtureMissingTooltip(sentinel.id);
  return (
    <li>
      <div
        role="option"
        aria-selected="false"
        aria-disabled="true"
        data-patient-id={sentinel.id}
        data-missing="true"
        data-testid="patient-picker-missing-row"
        title={tooltip}
        className="flex w-full cursor-not-allowed flex-col gap-gutter-sm rounded-signal-md border border-warning-strong-border bg-warning-strong-bg px-gutter-md py-gutter-md text-left opacity-90"
      >
        <div className="flex items-center justify-between gap-gutter-sm">
          <span
            className="text-signal-sm font-semibold text-warning-strong-fg"
            data-testid="patient-picker-missing-id"
          >
            {sentinel.id}
          </span>
          <span
            aria-hidden="true"
            className="rounded-signal-sm bg-warning-strong-fg px-1.5 py-0.5 text-signal-xs font-semibold uppercase tracking-wide text-text-inverse"
          >
            FIXTURE MISSING
          </span>
        </div>
        <p
          className="text-signal-xs text-warning-strong-fg"
          data-testid="patient-picker-missing-copy"
        >
          {tooltip}
        </p>
      </div>
    </li>
  );
}

export function PatientPicker({
  patients,
  activePatientId,
  onSelect,
  className,
}: PatientPickerProps) {
  return (
    <aside
      aria-labelledby="patient-picker-title"
      data-walkthrough-anchor="synthetic-patients"
      className={[
        'flex w-72 flex-col gap-gutter-md border-r border-surface-border bg-surface-muted p-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div>
        <h3
          id="patient-picker-title"
          className="text-signal-sm font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Patient list
        </h3>
        <p className="mt-gutter-sm text-signal-xs text-text-tertiary">
          {patients.length === 1
            ? '1 synthetic profile. Select to switch active chart.'
            : `${patients.length} synthetic profiles. Select to switch active chart.`}
        </p>
      </div>
      <ul role="listbox" aria-label="Synthetic patients" className="flex flex-col gap-gutter-sm">
        {patients.map((entry) => {
          if (isMissingPatientFixture(entry)) {
            return <MissingPatientRow key={entry.id} sentinel={entry} />;
          }
          return (
            <PatientRow
              key={entry.id}
              patient={entry}
              isActive={entry.id === activePatientId}
              onSelect={onSelect}
            />
          );
        })}
      </ul>
    </aside>
  );
}
